import { Text } from "@dropbox/dig-components/typography";
import { Link } from "@mirage/link/Link";
import { isValidUrl } from "@mirage/mosaics/SearchPage/Annotations/AnswersAnnotationModal/util/util";
import { openURL } from "@mirage/service-platform-actions";
import classNames from "classnames";
import React from "react";
import ReactMarkdown from "react-markdown";
import styles from "./FormattedText.module.css";
import { extendedEmojis } from "./utils/formattedTextEmojis";

const Bold: React.FC = ({ children }) => (
  <Text tagName="span" isBold={true} color="subtle">
    {children}
  </Text>
);

const BoldStandard: React.FC = ({ children }) => (
  <Text tagName="span" isBold={true} color="standard">
    {children}
  </Text>
);

const Paragraph: React.FC = ({ children }) => <>{children}</>;

const Code: React.FC = ({ children }) => {
  return (
    <span className={styles.code}>
      {children?.toString().replace(/\*/g, "")}
    </span>
  );
};

const Anchor: React.FC<{
  href: string;
  children: React.ReactNode;
  onClick?: () => void;
}> = ({ href, children, onClick }) => {
  if (isValidUrl(href)) {
    return (
      <Link
        href={href.replace(/\*/g, "")}
        target="_blank"
        onClick={(event) => {
          openURL(href.replace(/\*/g, ""));
          // Prevent the result row from being opened and only open link
          event.stopPropagation();
          event.preventDefault();
          onClick?.();
        }}
        rel="noreferrer"
      >
        {children}
      </Link>
    );
  } else {
    // if we are given an <em>text</em> within a link, we need to render it as a string
    const text = extractTextFromNode(children);
    return (
      <ReactMarkdown disallowedElements={["a"]} components={Components}>
        {text}
      </ReactMarkdown>
    );
  }
};

export type FormattedTextProps = {
  textToHighlight?: string;
  onClick?: () => void;
  textSize?: "small" | "medium" | "large";
  color?: "standard" | "subtle";
  sanitizeMarkdown?: boolean;
  numOfLines?: number;
  removeBoldText?: boolean;
};

function extractTextFromNode(
  element: string | React.ReactNode | undefined,
): string {
  try {
    if (element && typeof element !== "string") {
      const node = element as React.ReactElement;
      if (node.type === "strong") {
        return node.props.children.toString();
      }
    }
  } catch (e) {
    return element?.toString() ?? "";
  }
  return element?.toString() ?? "";
}

function cleanUpMarkdown(input: string, sanitize: boolean = false): string {
  input = replaceEmojiShortcodes(input);
  if (sanitize) {
    input = cleanBrokenMarkdownLinks(input);
    input = removeExtraMarkdown(input);
    return input;
  } else {
    return replaceEmTags(input);
  }
}

function replaceEmTags(input: string): string {
  return input.replace(/<\/?em>/g, "**");
}

function removeExtraMarkdown(input: string): string {
  return input
    .replace(/\*\*/g, "")
    .replace(/\* /g, " ")
    .replace(/ \*/g, " ")
    .replace(/```/g, "")
    .replace(/<\/?em>/g, "**")
    .replace(/>/g, "")
    .replace(/~~/g, "");
}

/*

  TODO: Remove this when we get plain text
  https://jira.dropboxer.net/browse/OSE-5359

*/
function cleanBrokenMarkdownLinks(text: string) {
  // broken markdown link at end: [text](incomplete...
  text = text.replace(/\[([^\]]+)\]\([^)]*$/, "$1");
  // Remove just a [text] or (url fragment at the start or end
  text = text.replace(/^(\s*)\[([^\]]*)$/, "$2");
  text = text.replace(/\[([^\]]*)$/, "$1");
  text = text.replace(/^(\s*)\([^)]*$/, "");
  text = text.replace(/\([^)]*$/, "");
  return text.trim();
}

function replaceEmojiShortcodes(text: string): string {
  return text.replace(/:([a-zA-Z0-9_+-]+):/g, (_, code) => {
    return extendedEmojis[`:${code}:`] || `:${code}:`;
  });
}

export const Components = {
  p: Paragraph,
  code: Code,
};

export const FormattedText = ({
  textToHighlight,
  textSize,
  onClick,
  color = "subtle",
  sanitizeMarkdown = false,
  numOfLines = 2,
  removeBoldText = false,
}: FormattedTextProps) => {
  if (removeBoldText) {
    textToHighlight = textToHighlight?.replace(/\*\*/g, "");
  }
  return (
    <Text
      color={color}
      size={textSize}
      className={classNames(styles.highlightContainer, {
        [styles.lineClamp1]: numOfLines === 1,
      })}
    >
      <ReactMarkdown
        components={{
          ...Components,
          bold: color === "standard" ? BoldStandard : Bold,
          em: color === "standard" ? BoldStandard : Bold,
          // eslint-disable-next-line react/no-unstable-nested-components
          a: (props) => <Anchor {...props} onClick={onClick} />,
        }}
      >
        {cleanUpMarkdown(textToHighlight || "", sanitizeMarkdown)}
      </ReactMarkdown>
    </Text>
  );
};
